import React, { useContext, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import StarekContext from "../context/StarekContext";
import Header from "../layouts/Header";
import Spinner from "../components/Spinner";
import "bootstrap/dist/css/bootstrap.min.css";
import "../starek.css";
import StarekPlansModal from "../components/modals/StarekPlansModal";
import ConfirmModal from "../components/modals/ConfirmModal";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import { gql, useQuery, useMutation } from "@apollo/client";
import { truncateString } from "../utils";
import { usePermissions } from "../hooks/usePermissions";

// Confirmation messages
const PLANT_MESSAGES = {
    DELETE_PLANT: (plantName) => `You are about to delete plant "${plantName}".

This action cannot be undone. All associated zones, shifts, and data will be permanently deleted.

Are you sure you wish to continue?`,
};

const GET_PLANTS_LIST = gql`
    query GetPlants {
        plants {
            id
            name
            description
            is_active
            zones {
                name
                id
                parent_id
                description
                created_at
            }
            shifts {
                name
                id
                start_time
                end_time
                runs_on
            }
        }
    }
`;

const CREATE_ONE_PLANT_MUTATION = gql`
    mutation CreateOnePlant($data: PlantCreateInput!) {
        createOnePlant(data: $data) {
            id
            name
            description
            created_by {
                name
            }
            updated_by {
                name
            }
        }
    }
`;

const UPDATE_ONE_PLANT_MUTATION = gql`
    mutation UpdateOnePlant(
        $data: PlantUpdateInput!
        $where: PlantWhereUniqueInput!
    ) {
        updateOnePlant(data: $data, where: $where) {
            id
            name
            description
            is_active
            updated_by {
                name
            }
        }
    }
`;

const DELETE_ONE_PLANT_MUTATION = gql`
    mutation DeleteOnePlant($where: PlantWhereUniqueInput!) {
        deleteOnePlant(where: $where) {
            id
            deleted_at
        }
    }
`;

const ChoosePlant = (props) => {
    const starekContext = useContext(StarekContext);
    const { can } = usePermissions();
    const canWrite = can("plant", "WRITE");
    const canDestroy = can("plant", "DESTROY");
    const hasAnyPlantAction = canWrite || canDestroy;
    const navigator = useNavigate();

    const membershipPlantIds = new Set(
        (starekContext.memberships || []).map(
            (m) => m.plant_id ?? m.plant?.id,
        ).filter(Boolean),
    );

    const { data, loading, error, refetch } = useQuery(GET_PLANTS_LIST, {
        client: starekContext.engineClient,
        onCompleted: (fetchedData) => {
            starekContext.setAvailablePlants(fetchedData.plants);
        },
    });

    const [createPlant, { loading: createLoading }] = useMutation(
        CREATE_ONE_PLANT_MUTATION,
        {
            client: starekContext.engineClient,
        },
    );

    const [updatePlant, { loading: updateLoading }] = useMutation(
        UPDATE_ONE_PLANT_MUTATION,
        {
            client: starekContext.engineClient,
        },
    );

    const [deletePlant, { loading: deleteLoading }] = useMutation(
        DELETE_ONE_PLANT_MUTATION,
        {
            client: starekContext.engineClient,
        },
    );

    const [slideOut, setSlideOut] = useState(false);
    const [showPlansModal, setShowPlansModal] = useState(false);
    const [showCreatePlantModal, setShowCreatePlantModal] = useState(false);
    const [plantName, setPlantName] = useState("");
    const [plantDescription, setPlantDescription] = useState("");
    const [plantNameError, setPlantNameError] = useState("");
    const [selectedPlantId, setSelectedPlantId] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [actionMenuPlantId, setActionMenuPlantId] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [editingPlantId, setEditingPlantId] = useState(null);
    const [editingPlantName, setEditingPlantName] = useState("");
    const [editingPlantDescription, setEditingPlantDescription] = useState("");
    const [originalPlantName, setOriginalPlantName] = useState("");
    const [originalPlantDescription, setOriginalPlantDescription] =
        useState("");
    const [loadingPlantId, setLoadingPlantId] = useState(null);
    const userClickedPlant = useRef(false);

    const handleShowPlansModal = () => setShowPlansModal(true);
    const handleClosePlansModal = () => setShowPlansModal(false);
    const handleShowCreatePlantModal = () => {
        setPlantName("");
        setPlantDescription("");
        setPlantNameError("");
        setShowCreatePlantModal(true);
    };
    const handleCloseCreatePlantModal = () => {
        setShowCreatePlantModal(false);
    };

    const handleCardClick = async (plantData) => {
        // Prevent selection of inactive plants
        if (!plantData.is_active) {
            return;
        }

        setSelectedPlantId(plantData.id);
        userClickedPlant.current = true;
        const alreadySelected = starekContext?.lastUsedPlant?.id === plantData.id;

        setTimeout(() => {
            starekContext.setLastUsedPlant(plantData);
            setSlideOut(true);

            if (alreadySelected) {
                setTimeout(() => navigator("/"), 300);
            }
        }, 600);
    };

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    const handleToggleActive = async (event, plantId, currentState) => {
        event.stopPropagation();
        setActionMenuPlantId(null);
        setLoadingPlantId(plantId);
        try {
            await updatePlant({
                variables: {
                    data: {
                        is_active: { set: !currentState },
                        updated_by: {
                            connect: { id: parseInt(starekContext.user.id) },
                        },
                    },
                    where: { id: plantId },
                },
            });
            refetch();
        } catch (err) {
            starekContext.showError(err);
        } finally {
            setLoadingPlantId(null);
        }
    };

    const handleStartEditPlantDetails = (event, plant) => {
        event.stopPropagation();
        setEditingPlantId(plant.id);
        setEditingPlantName(plant.name);
        setEditingPlantDescription(plant.description || "");
        setOriginalPlantName(plant.name);
        setOriginalPlantDescription(plant.description || "");
        setActionMenuPlantId(null);
    };

    const handleSavePlantDetails = async (plantId) => {
        const plant = plantsList.find((p) => p.id === plantId);
        if (!plant) return;

        // Build update data with only changed fields
        const updateData = {};

        if (editingPlantName.trim() !== plant.name) {
            updateData.name = { set: editingPlantName.trim() };
        }

        if (editingPlantDescription.trim() !== (plant.description || "")) {
            updateData.description = { set: editingPlantDescription.trim() };
        }

        // If nothing changed, just cancel
        if (Object.keys(updateData).length === 0) {
            setEditingPlantId(null);
            setEditingPlantName("");
            setEditingPlantDescription("");
            return;
        }

        try {
            updateData.updated_by = {
                connect: { id: parseInt(starekContext.user.id) },
            };

            await updatePlant({
                variables: {
                    data: updateData,
                    where: { id: plantId },
                },
            });
            setEditingPlantId(null);
            setEditingPlantName("");
            setEditingPlantDescription("");
            refetch();
        } catch (err) {
            console.error("Error saving plant details:", err);
            starekContext.showError(err.message || "Failed to update plant");
            setEditingPlantId(null);
            setEditingPlantName("");
            setEditingPlantDescription("");
        }
    };

    const handleCancelEditPlantDetails = () => {
        setEditingPlantId(null);
        setEditingPlantName("");
        setEditingPlantDescription("");
    };

    const handleDeletePlant = (event, plant) => {
        event.stopPropagation();
        setActionMenuPlantId(null);
        setLoadingPlantId(plant.id);
        openConfirm({
            title: "Delete Plant?",
            message: PLANT_MESSAGES.DELETE_PLANT(plant.name),
            confirmText: "Delete",
            cancelText: "Cancel",
            type: "danger",
            count: 1,
            onConfirm: async () => {
                try {
                    await deletePlant({
                        variables: {
                            where: { id: plant.id },
                        },
                    });
                    refetch();
                    closeConfirm();
                } catch (err) {
                    starekContext.showError(err);
                    closeConfirm();
                } finally {
                    setLoadingPlantId(null);
                }
            },
            onClose: () => {
                setLoadingPlantId(null);
                closeConfirm();
            },
            isLoading: deleteLoading,
        });
    };

    const handleStartEditPlantName = (event, plant) => {
        event.stopPropagation();
        setEditingPlantId(plant.id);
        setEditingPlantName(plant.name);
        setActionMenuPlantId(null);
    };

    const handleSavePlantName = async (plantId) => {
        if (!editingPlantName.trim()) {
            setEditingPlantId(null);
            setEditingPlantName("");
            return;
        }

        if (
            editingPlantName.trim() ===
            plantsList.find((p) => p.id === plantId)?.name
        ) {
            setEditingPlantId(null);
            setEditingPlantName("");
            return;
        }

        try {
            await updatePlant({
                variables: {
                    data: {
                        name: { set: editingPlantName.trim() },
                        updated_by: {
                            connect: { id: parseInt(starekContext.user.id) },
                        },
                    },
                    where: { id: plantId },
                },
            });
            setEditingPlantId(null);
            setEditingPlantName("");
            refetch();
        } catch (err) {
            starekContext.showError(err);
            setEditingPlantId(null);
            setEditingPlantName("");
        }
    };

    const handleCancelEditPlantName = () => {
        setEditingPlantId(null);
        setEditingPlantName("");
    };

    const handleCreatePlant = async (event) => {
        try {
            if (event) {
                event.preventDefault();
            }

            setPlantNameError("");

            if (!plantName.trim()) {
                setPlantNameError("Plant name is required");
                return;
            }

            await createPlant({
                variables: {
                    data: {
                        name: plantName.trim(),
                        description: plantDescription.trim(),
                        created_by: {
                            connect: { id: parseInt(starekContext.user.id) },
                        },
                        updated_by: {
                            connect: { id: parseInt(starekContext.user.id) },
                        },
                    },
                },
            });

            refetch();
            handleCloseCreatePlantModal();
        } catch (err) {
            starekContext.showError(err);
        }
    };

    const allPlants = data?.plants ?? starekContext.availablePlants ?? [];

    const plantsList =
        membershipPlantIds.size > 0
            ? allPlants.filter((p) => membershipPlantIds.has(p.id))
            : allPlants;

    const subscription = starekContext?.subscription;
    const legacyLicense = starekContext?.lastUsedWorkspace?.licenses?.[0];

    const planLabel = subscription?.planLabel
        ?? legacyLicense?.name
        ?? "Starter Plan";

    const plantsLimit = subscription?.limits?.plants
        ?? legacyLicense?.plantLicenses
        ?? 2;

    const devicesLimit = subscription?.limits?.devices
        ?? legacyLicense?.deviceLicenses
        ?? 10;

    const currentPlantCount = plantsList.length;
    const currentDeviceCount = subscription?.usage?.devices ?? null;

    const canCreateMorePlants =
        plantsLimit === -1 || currentPlantCount < plantsLimit;

    const nearingPlantLimit =
        plantsLimit !== -1 &&
        !canCreateMorePlants === false &&
        currentPlantCount >= plantsLimit - 1;

    // Filter and sort plants based on search text and active status
    const filteredPlantsList = plantsList
        .filter(
            (plant) =>
                plant.name.toLowerCase().includes(searchText.toLowerCase()) ||
                (plant.description &&
                    plant.description
                        .toLowerCase()
                        .includes(searchText.toLowerCase())),
        )
        .sort((a, b) => {
            // Active plants first, inactive plants last
            if (a.is_active === b.is_active) return 0;
            return a.is_active ? -1 : 1;
        });

    useEffect(() => {
        if (data?.plants) {
            starekContext.setAvailablePlants(data.plants);
        }
    }, [data]);

    useEffect(() => {
        if (starekContext?.lastUsedPlant?.id) {
            setSelectedPlantId(starekContext.lastUsedPlant.id);
        }
    }, []);

    useEffect(() => {
        if (
            userClickedPlant.current &&
            selectedPlantId &&
            starekContext?.lastUsedPlant?.id === selectedPlantId
        ) {
            setSlideOut(true);
            setTimeout(() => {
                const redirectPath =
                    sessionStorage.getItem("postAuthRedirect") || "/dashboard";
                // Bug fix: Redirect back to choose-plant & it caused a infinite loop between /dasbhoard & /choose-plant
                if (redirectPath === "/choose-plant") {
                    sessionStorage.removeItem("postAuthRedirect");
                    navigator("/dashboard");
                } else {
                    sessionStorage.removeItem("postAuthRedirect");
                    navigator(redirectPath);
                }
            }, 300);
        }
    }, [starekContext?.lastUsedPlant?.id, selectedPlantId]);

    return (
        <SwitchTransition>
            <CSSTransition
                key={slideOut ? "exit" : "enter"}
                timeout={300}
                classNames={{
                    enter: "page-crossfade-enter",
                    enterActive: "page-crossfade-enter-active",
                    exit: "page-crossfade-exit",
                    exitActive: "page-crossfade-exit-active",
                }}
            >
                <div className="transition-container">
                    <div className="choose-plant-page">
                        <div
                            style={{
                                maxWidth: "1400px",
                                margin: "0 auto",
                                width: "100%",
                                padding: "0 2rem",
                            }}
                        >
                            <Header hasLeftMargin={false} />
                        </div>
                        <div className="choose-plant-content">
                            <div className="choose-plant-container">
                                <div className="subscription-card">
                                    <div className="subscription-card-inner">
                                        <div className="subscription-info">
                                            <div className="subscription-plan-name">
                                                {planLabel}
                                            </div>
                                            <div className="subscription-usage">
                                                <div className="subscription-stat">
                                                    <i className="fa fa-leaf text-success"></i>
                                                    <span>
                                                        {currentPlantCount}
                                                        {" / "}
                                                        {plantsLimit === -1 ? "∞" : plantsLimit} plants
                                                    </span>
                                                </div>
                                                <div className="subscription-stat">
                                                    <i className="fa fa-microchip"></i>
                                                    <span>
                                                        {currentDeviceCount !== null
                                                            ? `${currentDeviceCount} / `
                                                            : ""}
                                                        {devicesLimit === -1 ? "∞" : devicesLimit} devices
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="subscription-badges">
                                                {nearingPlantLimit && (
                                                    <span className="badge bg-warning text-dark">
                                                        Nearing Plant Limit
                                                    </span>
                                                )}
                                                {!canCreateMorePlants && (
                                                    <span className="badge bg-danger">
                                                        Plant Limit Reached
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="subscription-actions">
                                            <button
                                                className={`btn ${!canCreateMorePlants ? "btn-warning" : "btn-outline-secondary btn-sm"}`}
                                                onClick={handleShowPlansModal}
                                                title="View available plans"
                                            >
                                                <i className="fa fa-crown me-2"></i>
                                                {!canCreateMorePlants ? "View / Upgrade Plan" : "View Plans"}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {!loading && plantsList.length > 0 && (
                                    <div>
                                        <h5
                                            className="plants-section-title"
                                            style={{ marginBottom: "1.5rem" }}
                                        >
                                            Plants
                                        </h5>
                                        <div
                                            style={{
                                                display: "flex",
                                                gap: "0.75rem",
                                                marginBottom: "1.5rem",
                                                alignItems: "flex-end",
                                            }}
                                        >
                                            <div style={{ flex: 1 }}>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Search plants..."
                                                    value={searchText}
                                                    onChange={(e) =>
                                                        setSearchText(
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            </div>
                                            {canWrite && (
                                                <button
                                                    className="btn btn-primary"
                                                    onClick={
                                                        handleShowCreatePlantModal
                                                    }
                                                    disabled={!canCreateMorePlants}
                                                    title={
                                                        !canCreateMorePlants
                                                            ? "Your workspace has reached its plant limit. Some plants may not be visible to you due to access permissions. Upgrade to create more."
                                                            : "Create a new plant"
                                                    }
                                                >
                                                    <i className="fa fa-plus me-2"></i>
                                                    Add Plant
                                                </button>
                                            )}                                        </div>
                                    </div>
                                )}

                                <div className="plants-grid">
                                    {loading ? (
                                        <div className="loading-state-card">
                                            <Spinner />
                                            <div className="loading-state-title">
                                                Loading Your Plants
                                            </div>
                                            <p className="loading-state-text">
                                                Please wait while we fetch your
                                                plants...
                                            </p>
                                        </div>
                                    ) : plantsList.length === 0 ? (
                                        <div className="empty-state-card">
                                            <div className="empty-state-icon">
                                                <i className="fa fa-industry"></i>
                                            </div>
                                            <h5 className="empty-state-title">
                                                No Plants Yet
                                            </h5>
                                            <p className="empty-state-text">
                                                You haven't created any plants
                                                yet. Get started by creating
                                                your first plant to begin
                                                managing your production.
                                            </p>
                                            <div className="empty-state-cta-group">
                                                {canWrite && (
                                                    <button
                                                        className="btn btn-primary"
                                                        onClick={
                                                            handleShowCreatePlantModal
                                                        }
                                                    >
                                                        <i className="fa fa-plus me-2"></i>
                                                        Create Your First Plant
                                                    </button>
                                                )}
                                                <button
                                                    className="btn btn-outline-info"
                                                    onClick={
                                                        handleShowPlansModal
                                                    }
                                                >
                                                    <i className="fa fa-crown me-2"></i>
                                                    View Plans
                                                </button>
                                            </div>
                                        </div>
                                    ) : searchText &&
                                        filteredPlantsList.length === 0 ? (
                                        <div className="empty-state-card">
                                            <div className="empty-state-icon">
                                                <i className="fa fa-search"></i>
                                            </div>
                                            <h5 className="empty-state-title">
                                                No Plants Found
                                            </h5>
                                            <p className="empty-state-text">
                                                No plants match your search "
                                                {searchText}". Try adjusting
                                                your search terms.
                                            </p>
                                            <button
                                                className="btn btn-outline-secondary mt-3"
                                                onClick={() =>
                                                    setSearchText("")
                                                }
                                            >
                                                <i className="fa fa-times me-2"></i>
                                                Clear Search
                                            </button>
                                        </div>
                                    ) : (
                                        filteredPlantsList.map((plant) => (
                                            <div
                                                key={plant.id}
                                                className={`card h-100 shadow-sm ${selectedPlantId === plant.id
                                                        ? "border-primary border-2"
                                                        : ""
                                                    } ${!plant.is_active
                                                        ? "opacity-75"
                                                        : ""
                                                    } ${plant.is_active &&
                                                        editingPlantId !== plant.id
                                                        ? "plant-card-clickable"
                                                        : ""
                                                    } ${actionMenuPlantId ===
                                                        plant.id
                                                        ? "plant-card-with-dropdown"
                                                        : ""
                                                    }`}
                                                onClick={(e) => {
                                                    if (
                                                        plant.is_active &&
                                                        editingPlantId !== plant.id
                                                    ) {
                                                        handleCardClick(plant);
                                                    }
                                                }}
                                            >
                                                {/* Card Header */}
                                                <div className="card-header bg-primary text-white">
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                                                            {editingPlantId ===
                                                                plant.id ? (
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm bg-white text-dark fw-bold border-0"
                                                                    value={
                                                                        editingPlantName
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) =>
                                                                        setEditingPlantName(
                                                                            e
                                                                                .target
                                                                                .value,
                                                                        )
                                                                    }
                                                                    onKeyDown={(
                                                                        e,
                                                                    ) => {
                                                                        if (
                                                                            e.key ===
                                                                            "Enter"
                                                                        ) {
                                                                            handleSavePlantDetails(
                                                                                plant.id,
                                                                            );
                                                                        } else if (
                                                                            e.key ===
                                                                            "Escape"
                                                                        ) {
                                                                            handleCancelEditPlantDetails();
                                                                        }
                                                                    }}
                                                                    onClick={(
                                                                        e,
                                                                    ) =>
                                                                        e.stopPropagation()
                                                                    }
                                                                    autoFocus
                                                                    disabled={
                                                                        updateLoading
                                                                    }
                                                                />
                                                            ) : (
                                                                <h5 className="card-title mb-0 text-truncate flex-grow-1">
                                                                    {plant.name}
                                                                </h5>
                                                            )}
                                                        </div>
                                                        {editingPlantId !==
                                                            plant.id && (
                                                                <div className="position-relative d-flex align-items-center gap-1">
                                                                    {loadingPlantId ===
                                                                        plant.id && (
                                                                            <div
                                                                                className="spinner-border spinner-border-sm text-white"
                                                                                role="status"
                                                                            >
                                                                                <span className="visually-hidden">
                                                                                    Loading...
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                    {hasAnyPlantAction && (
                                                                        <button
                                                                            className="btn btn-sm text-white border-0"
                                                                            onClick={(
                                                                                e,
                                                                            ) => {
                                                                                e.stopPropagation();
                                                                                setActionMenuPlantId(
                                                                                    actionMenuPlantId ===
                                                                                        plant.id
                                                                                        ? null
                                                                                        : plant.id,
                                                                                );
                                                                            }}
                                                                            title="Plant options"
                                                                        >
                                                                            <i className="fa fa-ellipsis-v"></i>
                                                                        </button>
                                                                    )}
                                                                    {actionMenuPlantId ===
                                                                        plant.id && (
                                                                            <div className="dropdown-menu dropdown-menu-end show plant-card-header-dropdown">
                                                                                {canWrite && (
                                                                                    <button
                                                                                        className="dropdown-item"
                                                                                        onClick={(
                                                                                            e,
                                                                                        ) =>
                                                                                            handleStartEditPlantDetails(
                                                                                                e,
                                                                                                plant,
                                                                                            )
                                                                                        }
                                                                                        disabled={
                                                                                            updateLoading
                                                                                        }
                                                                                    >
                                                                                        <i className="fa fa-edit me-2"></i>
                                                                                        Change
                                                                                        Details
                                                                                    </button>
                                                                                )}
                                                                                {selectedPlantId !==
                                                                                    plant.id && (
                                                                                        <>
                                                                                            {canWrite && (
                                                                                                <button
                                                                                                    className="dropdown-item"
                                                                                                    onClick={(
                                                                                                        e,
                                                                                                    ) =>
                                                                                                        handleToggleActive(
                                                                                                            e,
                                                                                                            plant.id,
                                                                                                            plant.is_active,
                                                                                                        )
                                                                                                    }
                                                                                                    disabled={
                                                                                                        updateLoading
                                                                                                    }
                                                                                                >
                                                                                                    <i
                                                                                                        className={`fa fa-${plant.is_active
                                                                                                                ? "ban"
                                                                                                                : "check"
                                                                                                            } me-2`}
                                                                                                    ></i>
                                                                                                    {plant.is_active
                                                                                                        ? "Make Inactive"
                                                                                                        : "Make Active"}
                                                                                                </button>
                                                                                            )}
                                                                                            {canWrite && canDestroy && (
                                                                                                <div className="dropdown-divider"></div>
                                                                                            )}
                                                                                            {canDestroy && (
                                                                                                <button
                                                                                                    className="dropdown-item text-danger"
                                                                                                    onClick={(
                                                                                                        e,
                                                                                                    ) =>
                                                                                                        handleDeletePlant(
                                                                                                            e,
                                                                                                            plant,
                                                                                                        )
                                                                                                    }
                                                                                                    disabled={
                                                                                                        deleteLoading
                                                                                                    }
                                                                                                >
                                                                                                    <i className="fa fa-trash me-2"></i>
                                                                                                    Delete
                                                                                                </button>
                                                                                            )}
                                                                                        </>
                                                                                    )}
                                                                            </div>
                                                                        )}
                                                                </div>
                                                            )}
                                                    </div>
                                                </div>

                                                {/* Card Body */}
                                                <div className="card-body d-flex flex-column plant-card-body">
                                                    {editingPlantId ===
                                                        plant.id ? (
                                                        <textarea
                                                            className="form-control form-control-sm flex-grow-1"
                                                            value={
                                                                editingPlantDescription
                                                            }
                                                            onChange={(e) =>
                                                                setEditingPlantDescription(
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            onKeyDown={(e) => {
                                                                if (
                                                                    e.key ===
                                                                    "Escape"
                                                                ) {
                                                                    handleCancelEditPlantDetails();
                                                                }
                                                            }}
                                                            onClick={(e) =>
                                                                e.stopPropagation()
                                                            }
                                                            disabled={
                                                                updateLoading
                                                            }
                                                            maxLength={191}
                                                            placeholder="Enter plant description..."
                                                            style={{
                                                                minHeight:
                                                                    "80px",
                                                                resize: "none",
                                                            }}
                                                        />
                                                    ) : (
                                                        <p
                                                            className="text-muted mb-0 flex-grow-1"
                                                            style={{
                                                                fontSize:
                                                                    "0.875rem",
                                                                overflow:
                                                                    "hidden",
                                                                textOverflow:
                                                                    "ellipsis",
                                                                display:
                                                                    "-webkit-box",
                                                                WebkitLineClamp: 3,
                                                                WebkitBoxOrient:
                                                                    "vertical",
                                                            }}
                                                        >
                                                            {plant.description ||
                                                                "No description provided"}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Card Footer */}
                                                <div
                                                    className="card-footer bg-light d-flex align-items-center plant-card-footer"
                                                    style={{
                                                        justifyContent:
                                                            editingPlantId ===
                                                                plant.id
                                                                ? "flex-end"
                                                                : "space-between",
                                                    }}
                                                >
                                                    {editingPlantId !==
                                                        plant.id && (
                                                            <div className="d-flex align-items-center gap-2">
                                                                <span
                                                                    className={`badge ${plant.is_active
                                                                            ? "bg-success"
                                                                            : "bg-secondary"
                                                                        }`}
                                                                >
                                                                    <i
                                                                        className={`fa fa-${plant.is_active
                                                                                ? "check-circle"
                                                                                : "ban"
                                                                            } me-1`}
                                                                    ></i>
                                                                    {plant.is_active
                                                                        ? "Active"
                                                                        : "Inactive"}
                                                                </span>
                                                                {selectedPlantId ===
                                                                    plant.id && (
                                                                        <span className="badge bg-primary">
                                                                            <i className="fa fa-check me-1"></i>
                                                                            Selected
                                                                        </span>
                                                                    )}
                                                            </div>
                                                        )}

                                                    {editingPlantId ===
                                                        plant.id && (
                                                            <div
                                                                className="d-flex gap-2"
                                                                onClick={(e) =>
                                                                    e.stopPropagation()
                                                                }
                                                            >
                                                                {canWrite && (
                                                                    <button
                                                                        className="btn btn-sm btn-success"
                                                                        onClick={(
                                                                            e,
                                                                        ) => {
                                                                            e.stopPropagation();
                                                                            handleSavePlantDetails(
                                                                                plant.id,
                                                                            );
                                                                        }}
                                                                        disabled={
                                                                            updateLoading
                                                                        }
                                                                        title="Save changes"
                                                                    >
                                                                        <i className="fa fa-check"></i>
                                                                    </button>
                                                                )}
                                                                <button
                                                                    className="btn btn-sm btn-secondary"
                                                                    onClick={(
                                                                        e,
                                                                    ) => {
                                                                        e.stopPropagation();
                                                                        handleCancelEditPlantDetails();
                                                                    }}
                                                                    disabled={
                                                                        updateLoading
                                                                    }
                                                                    title="Cancel"
                                                                >
                                                                    <i className="fa fa-times"></i>
                                                                </button>
                                                            </div>
                                                        )}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {showCreatePlantModal && (
                        <div
                            className="modal fade show"
                            style={{
                                display: "block",
                                backgroundColor: "rgba(0,0,0,0.5)",
                            }}
                            tabIndex="-1"
                            role="dialog"
                            aria-labelledby="createPlantModal"
                            aria-hidden="true"
                        >
                            <div
                                className="modal-dialog modal-lg modal-dialog-centered"
                                role="document"
                            >
                                <div className="modal-content rounded-lg shadow-lg">
                                    <div className="modal-header bg-light border-bottom">
                                        <h5
                                            className="modal-title font-weight-bold text-primary"
                                            id="createPlantModal"
                                        >
                                            <i className="fa fa-plus me-2"></i>
                                            Create New Plant
                                        </h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={
                                                handleCloseCreatePlantModal
                                            }
                                            aria-label="Close"
                                        ></button>
                                    </div>
                                    <div className="modal-body p-4">
                                        <form
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                handleCreatePlant();
                                            }}
                                        >
                                            <div className="mb-3">
                                                <label
                                                    htmlFor="plantName"
                                                    className="form-label fw-600"
                                                >
                                                    Plant Name{" "}
                                                    <span className="text-danger">
                                                        *
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${plantNameError
                                                            ? "is-invalid"
                                                            : ""
                                                        }`}
                                                    id="plantName"
                                                    placeholder="e.g., Factory A, Main Plant"
                                                    value={plantName}
                                                    onChange={(e) => {
                                                        setPlantName(
                                                            e.target.value,
                                                        );
                                                        if (plantNameError)
                                                            setPlantNameError(
                                                                "",
                                                            );
                                                    }}
                                                />
                                                {plantNameError && (
                                                    <div className="invalid-feedback d-block">
                                                        {plantNameError}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="mb-4">
                                                <label
                                                    htmlFor="plantDescription"
                                                    className="form-label fw-600"
                                                >
                                                    Description
                                                </label>
                                                <textarea
                                                    className="form-control"
                                                    id="plantDescription"
                                                    rows="4"
                                                    placeholder="Optional: Describe your plant location, purpose, or other relevant details"
                                                    value={plantDescription}
                                                    onChange={(e) =>
                                                        setPlantDescription(
                                                            e.target.value,
                                                        )
                                                    }
                                                    maxLength={191}
                                                ></textarea>
                                                <small className="text-muted d-block mt-2">
                                                    <i className="fa fa-info-circle me-1"></i>
                                                    You can configure zones,
                                                    shifts, cycles, and other
                                                    details after creation
                                                </small>
                                            </div>
                                        </form>
                                    </div>
                                    <div className="modal-footer bg-light border-top">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={
                                                handleCloseCreatePlantModal
                                            }
                                            disabled={createLoading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleCreatePlant}
                                            disabled={createLoading}
                                        >
                                            {createLoading ? (
                                                <>
                                                    <span
                                                        className="spinner-border spinner-border-sm me-2"
                                                        role="status"
                                                        aria-hidden="true"
                                                    ></span>
                                                    Creating...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa fa-check me-2"></i>
                                                    Create Plant
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Plans Modal */}
                    {showPlansModal && (
                        <StarekPlansModal
                            show={showPlansModal}
                            handleClose={handleClosePlansModal}
                        />
                    )}

                    {/* Delete Confirmation Modal */}
                    {confirmCfg.open && (
                        <ConfirmModal
                            open={confirmCfg.open}
                            type={confirmCfg.type}
                            title={confirmCfg.title}
                            message={confirmCfg.message}
                            confirmText={confirmCfg.confirmText}
                            cancelText={confirmCfg.cancelText}
                            onConfirm={confirmCfg.onConfirm}
                            onClose={confirmCfg.onClose}
                            isLoading={confirmCfg.isLoading}
                        />
                    )}
                </div>
            </CSSTransition>
        </SwitchTransition>
    );
};

export default ChoosePlant;
